import clsx from 'clsx'
import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { AUGMENTT_POSTURE_STATES, MICROSOFT_POSTURE_STATES } from './constants'
import styles from './ResolveCheckMenu.module.scss'

const { checkIcon } = styles

const MENU_OPTIONS = [
  {
    messageId: 'SECURITY_CHECK.STATE.AUGMENTT_COMPLIANCE',
    state: MICROSOFT_POSTURE_STATES.DEFAULT,
  },
  {
    messageId: 'SECURITY_CHECK.STATE.RISK_ACCEPTED',
    state: MICROSOFT_POSTURE_STATES.RISK_ACCEPTED,
  },
  {
    messageId: 'SECURITY_CHECK.STATE.PLANNED',
    state: MICROSOFT_POSTURE_STATES.PLANNED,
  },
  {
    messageId: 'SECURITY_CHECK.STATE.THIRD_PARTY',
    state: MICROSOFT_POSTURE_STATES.THIRD_PARTY,
  },
  {
    messageId: 'SECURITY_CHECK.STATE.ALTERNATE_MITIGATION',
    state: MICROSOFT_POSTURE_STATES.ALTERNATE_MITIGATION,
  },
]

const formatMenuLabel = (messageId, state) =>
  function (row) {
    const formatted = <FormattedMessage id={messageId} />
    const currentState = row?.msSecureScore?.scoreControlState || MICROSOFT_POSTURE_STATES.DEFAULT
    if (currentState === state) {
      return (
        <span className="fw-bold">
          {formatted}
          <i className={clsx('fas fa-check ms-2', checkIcon)} />
        </span>
      )
    }
    return formatted
  }

/**
 * Override options are disabled on already-compliant checks (nothing to resolve).
 * Augmentt Compliance Status stays enabled when there is no custom status (current default).
 */
const isMenuOptionDisabled = (state) => (row) => {
  if (state === MICROSOFT_POSTURE_STATES.DEFAULT) {
    return false
  }
  return row?.status === AUGMENTT_POSTURE_STATES.COMPLIANT
}

/**
 * Whether the resolve-check menu should appear for a posture row.
 * Available for measurable monitored checks (Secure Score–mapped and Augmentt-only).
 * @param {object} row
 * @returns {boolean|string|undefined}
 */
export const showResolveCheckMenu = (row) => row?.checkId && row?.status !== AUGMENTT_POSTURE_STATES.NOT_MEASURED

/**
 * Shared resolve-check menu options (Compliance Audit status badge + Legacy Audit ⋯ menu).
 *
 * @param {object} params
 * @param {(payload: { securityCheck: object, initialStatus: string, show: boolean }) => void} params.openResolutionModal
 * @param {boolean} [params.enabled=true] When false, `menu` is null (e.g. read-only / no modal surface).
 * @returns {{ menu: Array<object>|null, showMenu: typeof showResolveCheckMenu }}
 */
const useResolveCheckMenu = ({ openResolutionModal, enabled = true }) => {
  const intl = useIntl()

  const menu = useMemo(() => {
    if (!enabled || !openResolutionModal) {
      return null
    }

    const onOpenResolutionModal = (selectedStatus) => (row) => {
      openResolutionModal({
        securityCheck: row,
        initialStatus: selectedStatus,
        show: true,
      })
    }

    const disableLabel = intl.formatMessage({
      id: 'SECURE.NO_CHANGE_COMPLIANT',
    })

    return MENU_OPTIONS.map(({ messageId, state }) => ({
      label: formatMenuLabel(messageId, state),
      onClick: onOpenResolutionModal(state),
      isDisabled: isMenuOptionDisabled(state),
      disableLabel,
    }))
  }, [enabled, intl, openResolutionModal])

  return {
    menu,
    showMenu: showResolveCheckMenu,
  }
}

export default useResolveCheckMenu
